import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { endOrderTiming } from '../lib/orderTiming';
import { newPositionSize } from '../lib/positionHelpers';
import { invalidateQueries } from '../lib/reactQueryHelpers';
import { showToast } from '../lib/showToast';
import { batchedFilledOrderToToastMessage } from '../lib/toasts';
import { OrderFills } from '../types/fills';
import { fillsQuery } from './useFills';
import { positionsQuery } from './usePositions';
import { useProducts } from './useProducts';
import { useActiveSelectedSubaccount } from './useSelectedSubaccount';
import { subaccountBalanceQuery } from './useSubaccountBalance';
import { tradesQuery } from './useTrades';
import { useOrderFill } from './useWebsocketStore';

type UpdatePositionsParams = {
  subaccountId: string;
  fill: OrderFills['data'][number];
  queryClient: QueryClient;
};
export const updatePositions = ({
  subaccountId,
  fill,
  queryClient,
}: UpdatePositionsParams): void => {
  const openPositionsQueryOptions = positionsQuery({ subaccountId, open: true });
  queryClient.setQueryData(openPositionsQueryOptions.queryKey, (positions) => {
    if (!positions) return positions;

    return {
      ...positions,
      pages: positions.pages.map((page) => ({
        ...page,
        data: page.data
          .map((position) => {
            if (fill.productId !== position.productId) return position;

            return {
              ...position,
              size: newPositionSize(position.size, fill.filled, fill.side === 0 ? 'long' : 'short'),
            };
          })
          .filter((position) => position.size !== 0n),
      })),
    };
  });

  // Mark the open and close position query as stale to trigger a background refetch no matter previous actions.
  invalidateQueries(queryClient, positionsQuery({ subaccountId }).queryKey);
};

export const useFillsSub = () => {
  const selectedSubaccount = useActiveSelectedSubaccount();
  const queryClient = useQueryClient();
  const currentSubaccountId = selectedSubaccount?.id;
  const { data: products } = useProducts();

  const handleFill = useCallback(
    (fills: OrderFills) => {
      if (!currentSubaccountId) return;
      const product = products?.find((p) => p.id === fills.data[0]?.productId);
      if (!product) return;

      const batchedToastMessage = batchedFilledOrderToToastMessage(product, fills);
      if (batchedToastMessage) {
        showToast(batchedToastMessage);
      }
      const firstFill = fills.data[0];
      if (!firstFill) {
        throw new Error('Expected at least one fill');
      }
      // All fills should be from the same order.
      endOrderTiming(firstFill.orderId);

      updatePositions({
        subaccountId: currentSubaccountId,
        fill: firstFill,
        queryClient,
      });

      invalidateQueries(queryClient, fillsQuery({ subaccountId: currentSubaccountId }).queryKey);

      invalidateQueries(queryClient, tradesQuery({ productId: product.id }).queryKey);

      invalidateQueries(queryClient, subaccountBalanceQuery(currentSubaccountId).queryKey);
    },
    [currentSubaccountId, products, queryClient]
  );

  useOrderFill(currentSubaccountId, handleFill);
};
